import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Input, Tooltip } from '@fluentui/react-components';
import {
  AddCircleRegular,
  CopyRegular,
  CheckmarkRegular,
  Dismiss24Regular,
  EditRegular,
  GridRegular,
  SaveRegular,
  SearchRegular
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import * as ComponentFunctions from './PriceListHeader.Functions';
import './PriceListHeader.css';

const PriceListHeaderComponentBase = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [activePriceListTitleForm, setActivePriceListTitleForm] = useState(false);
  const [massiveUpdateActive, setMassiveUpdateActive] = useState(false);
  const [priceListTitle, setPriceListTitle] = useState('');
  const [displayName, setDisplayName] = useState(props.PriceList.priceList.listName);

  useEffect(() => {
    setDisplayName(props.PriceList.priceList.listName);
    setPriceListTitle('');
    setActivePriceListTitleForm(false);
  }, [props.PriceList.id, props.PriceList.priceList.listName]);

  useEffect(() => {
    setDisplayName(props.PriceList.priceList.listName);
  }, [props.PriceList.priceList.listName]);

  // ── Events ─────────────────────────────────────────────────────────────────

  const HandlerAddPriceClick = () => {
    if (props.onAddMedicalService) props.onAddMedicalService();
  };

  const HandlerClonePriceClick = () => {
    props.onClonePriceList(props.PriceList.id);
  };

  const HandlerMassiveUpdateClick = () => {
    props.onMassiveUpdateChange(!massiveUpdateActive);
    setMassiveUpdateActive(!massiveUpdateActive);
  };

  const HandlerDeletePriceClick = () => {
    props.onDeletePriceList(props.PriceList.id);
  };

  const HandlerUpdatePriceListTitle = () => {
    const trimmed = priceListTitle?.trim();
    if (trimmed) {
      const updatedPriceList = {
        ...props.PriceList.priceList,
        listName: trimmed
      };
      props.UpdatePriceList(updatedPriceList);
      setDisplayName(trimmed);
      setActivePriceListTitleForm(false);
    }
  };

  const HandlerCancelTitleEdit = () => {
    setPriceListTitle('');
    setActivePriceListTitleForm(false);
  };

  const onSearchService = (searchText: string | undefined) => {
    if (props.onSearchService) props.onSearchService(searchText);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="priceListHeader">
      <div className="priceListHeader-titleRow">
        {!activePriceListTitleForm && (
          <>
            <div className="priceListHeader-title" title={displayName}>
              {displayName}
            </div>
            <div className="priceListHeader-titleActions">
              <Tooltip
                content={t('priceListHeader.buttons.edit')}
                relationship="label"
                withArrow
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<EditRegular />}
                  aria-label={t('priceListHeader.buttons.edit')}
                  onClick={() => {
                    setPriceListTitle(displayName);
                    setActivePriceListTitleForm(true);
                  }}
                />
              </Tooltip>
            </div>
          </>
        )}
        {activePriceListTitleForm && (
          <>
            <Input
              className="priceListHeader-titleInput"
              value={priceListTitle}
              autoFocus
              onChange={(_, data) => setPriceListTitle(data.value)}
            />
            <div className="priceListHeader-titleActions">
              <Tooltip
                content={t('priceListHeader.buttons.confirm')}
                relationship="label"
                withArrow
              >
                <Button
                  appearance="primary"
                  size="small"
                  icon={<CheckmarkRegular />}
                  aria-label={t('priceListHeader.buttons.confirm')}
                  onClick={HandlerUpdatePriceListTitle}
                />
              </Tooltip>
              <Tooltip
                content={t('priceListHeader.buttons.cancel')}
                relationship="label"
                withArrow
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Dismiss24Regular />}
                  aria-label={t('priceListHeader.buttons.cancel')}
                  onClick={HandlerCancelTitleEdit}
                />
              </Tooltip>
            </div>
          </>
        )}
      </div>

      <div className="priceListHeader-actionsRow">
        <Input
          className="priceListHeader-search"
          type="search"
          placeholder={t('priceListHeader.buttons.searchService')}
          contentBefore={<SearchRegular />}
          onChange={(_, data) => onSearchService(data.value || undefined)}
          aria-label={t('priceListHeader.buttons.searchService')}
          size="medium"
        />

        <div className="priceListHeader-spacer" />

        <div className="priceListHeader-actionsGroup">
          <Tooltip
            content={t('priceListHeader.buttons.modifyPricesMassively')}
            relationship="label"
            withArrow
          >
            <Button
              appearance={massiveUpdateActive ? 'primary' : 'secondary'}
              icon={massiveUpdateActive ? <SaveRegular /> : <GridRegular />}
              aria-label={t('priceListHeader.buttons.modifyPricesMassively')}
              onClick={HandlerMassiveUpdateClick}
            />
          </Tooltip>
          <Tooltip
            content={t('priceListHeader.buttons.clonePrice')}
            relationship="label"
            withArrow
          >
            <Button
              appearance="secondary"
              icon={<CopyRegular />}
              aria-label={t('priceListHeader.buttons.clonePrice')}
              onClick={HandlerClonePriceClick}
            />
          </Tooltip>

          <span className="priceListHeader-divider" aria-hidden="true" />

          <Button
            appearance="primary"
            icon={<AddCircleRegular />}
            onClick={HandlerAddPriceClick}
          >
            {t('priceListHeader.buttons.addPrice')}
          </Button>
          <ConfirmationButtonComponent
            ButtonId={'PriceListDeleteButton' + props.PriceList.id}
            ButtonIconName={'Delete'}
            ButtonText={t('priceListHeader.buttons.deleteButton')}
            ButtonCssClass={'deleteButton'}
            ConfirmationButtonText={t('priceListHeader.buttons.deleteButtonText')}
            ConfirmationButtonCssClass={'deleteButton'}
            CancelButtonText={t('priceListHeader.buttons.cancelButtonText')}
            CancelButtonCssClass={''}
            Title={t('priceListHeader.buttons.deletePriceListInformation')}
            Description={t('priceListHeader.buttons.descriptionDeletePriceList')}
            ElementTextHeader={t('priceListHeader.buttons.deletePriceList')}
            ElementTextInformation={props.PriceList.priceList.listName}
            onConfirmation={HandlerDeletePriceClick}
            onCancel={props.onCancelDeletePriceList}
            isOperationExecuting={props.isDeletingExecution}
            isOperationExecutedWithError={props.FailOnDeleting}
            OperationError={props.ErrorOnDeleting}
          />
        </div>
      </div>
    </div>
  );
};

export const PriceListHeaderComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PriceListHeaderComponentBase as any) as any;

export default PriceListHeaderComponent;
